// Каршеринг портала 73: парк, поездки, заправки. ТОЛЬКО ЧТЕНИЕ.
//
// ЗАЧЕМ ОТДЕЛЬНЫЙ РАЗДЕЛ, ЕСЛИ ВКЛАДКА «КАРШЕРИНГ» УЖЕ ЕСТЬ В ОВЕРТАЙМАХ
// Вкладка отвечает на один вопрос — «кто сейчас в пути» — и живёт внутри раздела,
// закрытого на одну учётку владельца, потому что рядом с ней заработок людей. А в
// каршеринге два разных учёта, и открывать их можно по-разному:
//   • служебная машина — имущество компании. Её пробег, состояние и загрузка это учёт
//     вещи, а не наблюдение за человеком;
//   • личная машина — компенсация расходов сотруднику. Там важна сумма, а не маршрут,
//     и это тот же разряд данных, что переработка.
// Разведя их по вкладкам одного раздела с разными ручками ядра, мы даём владельцу
// возможность решить про каждую половину отдельно, а не «весь p73x целиком».
//
// ЧЕГО ЗДЕСЬ НЕ УВИДЯТ НИКОГДА
// Госномера и марки личного авто, адреса личных поездок, координат выезда, штрафов и
// фотографий. Первые три закрыты масками в SELECT ядра (p73x_api: ГОСНОМЕР, МАШИНА,
// НАЗНАЧЕНИЕ), остальных нет даже в зеркале — их не читает синк. Экран на маски не
// полагается молча: там, где текст закрыт, он говорит, что закрыт.
//
// Замер 28.07.2026 по боевой базе, ради него раздел и делается: в зеркале 1472 поездки
// за год, 98 машин (24 служебные и 74 личные), 103 заправки, 78 водителей — и до сих пор
// всё это видела одна учётка.
import { useMemo, useState } from 'react'

import {
  ОГОВОРКИ_КАРШЕРИНГА,
  ИМЯ_СУЩНОСТИ,
  РАЗМЕР_СТРАНИЦЫ,
  самаяСтарая,
  useЗаправки,
  useПарк,
  useПоездкиКарш,
  useСправочникиКарш,
} from '../api/carsharing'
import type {
  ЗаправкаСтрока,
  ЗаправкиИтоги,
  МашинаСтрока,
  ПаркИтоги,
  ПоездкаСтрока,
  ПоездкиИтоги,
} from '../api/carsharing'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtMoney, pluralN } from '../lib/format'

type Вкладка = 'park' | 'trips' | 'refuels'

export default function Carsharing() {
  const [вкладка, setВкладка] = useState<Вкладка>('park')
  const refs = useСправочникиКарш()

  const св = самаяСтарая(refs.data?.freshness)
  const счёт = refs.data?.counts

  return (
    <>
      <div className="page-head">
        <h1>Каршеринг</h1>
        <span className="spacer" />
        <span className="hint">
          {св
            ? `обновлено ${(св.age_min ?? 0) < 1 ? 'только что' : `${Math.round(св.age_min ?? 0)} мин назад`}`
              + ` (позже всех — ${ИМЯ_СУЩНОСТИ[св.entity] ?? св.entity})`
            : 'свежесть неизвестна'}
        </span>
      </div>

      <div className="notice" style={{ marginBottom: 12 }}>
        <b>Госномера и марки личных машин здесь не показываются, адреса личных поездок —
          тоже.</b> Это правило владельца, и закрыто оно в ядре, а не на экране: как бы
        раздел ни спросили, их не отдадут. Служебные машины видны целиком — это имущество
        компании. Записи заводят инженеры на портале 73, в CRM только обзор.
      </div>

      {/* Переключатель вкладок — обычный flex, а НЕ .filters: на телефоне правило
          «.filters > * { width: 100% }» поставило бы кнопки в столбец. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {([
          ['park', `Парк${счёт ? ` · ${счёт.cars_service} служебных` : ''}`],
          ['trips', `Поездки${счёт ? ` · в пути ${счёт.car_trips_open}` : ''}`],
          ['refuels', `Заправки личных${счёт ? ` · ${счёт.refuels}` : ''}`],
        ] as [Вкладка, string][]).map(([код, подпись]) => (
          <button key={код} onClick={() => setВкладка(код)}
                  aria-pressed={вкладка === код}
                  style={{ fontWeight: вкладка === код ? 600 : 400, flex: '0 1 auto' }}>
            {подпись}
          </button>
        ))}
      </div>

      <QueryState isLoading={refs.isLoading} error={refs.error}>
        {вкладка === 'park' && <Парк регионы={refs.data?.car_regions} />}
        {вкладка === 'trips' && <Поездки регионы={refs.data?.trip_regions} />}
        {вкладка === 'refuels' && <Заправки />}
        <Оговорки заметки={refs.data?.data_notes} />
      </QueryState>
    </>
  )
}

/** Оговорки из ядра. Тексты и числа в них считает то же ядро, что и данные рядом. */
function Оговорки({ заметки }: { заметки?: Record<string, string> }) {
  if (!заметки) return null
  const важные = ОГОВОРКИ_КАРШЕРИНГА.filter((к) => заметки[к])
  if (!важные.length) return null
  return (
    <div className="card hint" style={{ marginTop: 12 }}>
      <b>Что нужно знать об этих данных</b>
      <ul className="plain-list" style={{ marginTop: 6 }}>
        {важные.map((к) => <li key={к}>{заметки[к]}</li>)}
      </ul>
    </div>
  )
}

/** Пометка ядра «в ответе деньги нескольких людей». Показываем, а не прячем. */
function Закрыто({ почему }: { почему?: string }) {
  if (!почему) return null
  return (
    <div className="notice" style={{ marginBottom: 12 }}>
      <span className="hint">
        Выборка по всем сотрудникам: {почему}. Раздел открыт не всем — не показывайте
        экран через плечо.
      </span>
    </div>
  )
}

function Страницы({ всего, start, setStart }: {
  всего: number; start: number; setStart: (v: number) => void
}) {
  if (всего <= РАЗМЕР_СТРАНИЦЫ) return null
  return (
    <div className="pager">
      <button disabled={start === 0}
              onClick={() => setStart(Math.max(0, start - РАЗМЕР_СТРАНИЦЫ))}>
        назад
      </button>
      <span className="muted">
        {start + 1}–{Math.min(start + РАЗМЕР_СТРАНИЦЫ, всего)} из {всего}
      </span>
      <button disabled={start + РАЗМЕР_СТРАНИЦЫ >= всего}
              onClick={() => setStart(start + РАЗМЕР_СТРАНИЦЫ)}>
        дальше
      </button>
    </div>
  )
}

/** Список ключей справочника по убыванию количества. Порядок задаёт ядро, не экран. */
function ключи(справочник?: Record<string, number>): string[] {
  return Object.keys(справочник ?? {}).filter((к) => к && к !== '—')
}

// ═══════════════════════════════ парк ═══════════════════════════════
//
// Главный экран раздела, и по умолчанию здесь ТОЛЬКО СЛУЖЕБНЫЕ машины. Не потому, что
// личные прячем — они в одном переключателе отсюда, — а потому что это разные вопросы:
// «что с нашими машинами» и «кому мы компенсируем бензин». Смешанный список отвечает
// на оба сразу и ни на один толком.

function Парк({ регионы }: { регионы?: Record<string, number> }) {
  const [личные, setЛичные] = useState(false)
  const [регион, setРегион] = useState('')
  const [start, setStart] = useState(0)

  const фильтр = useMemo(() => ({
    kind: личные ? '' : 'service',
    region: регион,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [личные, регион, start])

  const запрос = useПарк(фильтр)
  const строки = запрос.data?.rows ?? []
  const итоги = запрос.data?.totals
  const окно = запрос.data?.window_days ?? 0

  const сСброса = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }

  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <FilterBar id="carsharing-park">
          <label htmlFor="carsharing-park-region">
            Регион
            <select id="carsharing-park-region" value={регион}
                    onChange={(e) => сСброса(setРегион)(e.target.value)}>
              <option value="">все</option>
              {ключи(регионы).map((к) => <option key={к} value={к}>{к}</option>)}
            </select>
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={личные}
                   onChange={(e) => сСброса(setЛичные)(e.target.checked)} />
            показать и личные авто
          </label>
        </FilterBar>
        <div className="hint" style={{ marginTop: 8 }}>
          найдено: {запрос.data?.total ?? 0}
          {итоги ? ` · служебных ${итоги.service_n ?? 0} · личных ${итоги.personal_n ?? 0}` : ''}
        </div>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        {итоги && <ПлиткиПарка итоги={итоги} окно={окно} />}
        <ТаблицаПарка строки={строки} окно={окно} />
        <Страницы всего={запрос.data?.total ?? 0} start={start} setStart={setStart} />
      </QueryState>
    </>
  )
}

function ПлиткиПарка({ итоги, окно }: { итоги: ПаркИтоги; окно: number }) {
  const свободно = (итоги.service_n ?? 0) - (итоги.busy_n ?? 0)
  return (
    <div className="tiles" style={{ marginBottom: 12 }}>
      <div className="tile">
        <div className="t-label">служебных машин</div>
        <div className="t-sum">{итоги.service_n ?? 0}</div>
        <div className="t-label">в разъездах: {итоги.busy_n ?? 0}, свободно: {свободно}</div>
      </div>
      <div className="tile">
        <div className="t-label">личных авто в учёте</div>
        <div className="t-sum">{итоги.personal_n ?? 0}</div>
        <div className="t-label">номера и марки закрыты правилом</div>
      </div>
      <div className="tile">
        <div className="t-label">числятся действующими</div>
        <div className="t-sum">{итоги.active_n ?? 0}</div>
        <div className="t-label">из {итоги.n ?? 0} в списке</div>
      </div>
      <div className="tile">
        <div className="t-label">со сроками ОСАГО и ТО</div>
        <div className="t-sum">{итоги.osago_known ?? 0}</div>
        <div className="t-label">
          сроки в портале не ведут, {pluralN(окно, 'день', 'дня', 'дней')} — окно загрузки
        </div>
      </div>
    </div>
  )
}

function ТаблицаПарка({ строки, окно }: { строки: МашинаСтрока[]; окно: number }) {
  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">Машин под этот отбор нет</div>
        <div className="empty-hint">
          Парк ведут на портале 73: машину заводят там, в зеркало она приезжает ближайшим
          прогоном. Снимите фильтр по региону или включите личные авто.
        </div>
      </div>
    )
  }
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th>Машина</th>
            <th>Регион</th>
            <th>Состояние</th>
            <th className="num">Одометр</th>
            <th className="num">За {окно} дн.</th>
            <th>Последняя поездка</th>
          </tr>
        </thead>
        <tbody>
          {строки.map((r) => <СтрокаМашины key={r.d1_id} r={r} />)}
        </tbody>
      </table>
    </div>
  )
}

function СтрокаМашины({ r }: { r: МашинаСтрока }) {
  const личная = r.kind === 'personal'
  return (
    <tr>
      <td data-label="Машина">
        <div>{r.title ?? '—'}</div>
        {личная
          ? <div className="hint">номер и марка не показываются: личное авто сотрудника</div>
          : <div className="muted mono">{r.plate ?? 'номер не заполнен'}</div>}
      </td>
      <td data-label="Регион">{r.region ?? <span className="hint">не указан</span>}</td>
      <td data-label="Состояние">
        {личная
          ? <span className="hint">личное авто</span>
          : <span className={r.status === 'busy' ? 'badge work' : 'badge'}>
              {r.status === 'busy' ? 'в разъезде' : 'свободна'}
            </span>}
        {(r.trips_open ?? 0) > 0 && (
          <div className="hint">открытых поездок: {r.trips_open}</div>
        )}
        {r.active === false && <div className="hint">снята с учёта в портале</div>}
        {r.no_fuel_card && <div className="hint">без топливной карты, расход по пробегу</div>}
      </td>
      <td className="num" data-label="Одометр">
        {r.last_odometer ?? <span className="hint">неизвестен</span>}
      </td>
      <td className="num" data-label="Поездок и км">
        <div>{r.trips_window ?? 0}</div>
        <div className="hint">{r.km_window ?? 0} км</div>
      </td>
      <td data-label="Последняя поездка">
        {r.last_trip_at
          ? fmtDate(r.last_trip_at, true)
          : <span className="hint">поездок в зеркале нет</span>}
      </td>
    </tr>
  )
}

// ═══════════════════════════════ поездки ═══════════════════════════════

function Поездки({ регионы }: { регионы?: Record<string, number> }) {
  const [вПути, setВПути] = useState(false)
  const [вид, setВид] = useState('')
  const [регион, setРегион] = useState('')
  const [поиск, setПоиск] = useState('')
  const [start, setStart] = useState(0)

  const фильтр = useMemo(() => ({
    status: вПути ? 'open' : '',
    kind: вид,
    region: регион,
    q: поиск,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [вПути, вид, регион, поиск, start])

  const запрос = useПоездкиКарш(фильтр)
  const строки = запрос.data?.rows ?? []
  const итоги = запрос.data?.totals

  const сСброса = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }

  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <FilterBar id="carsharing-trips">
          <label htmlFor="carsharing-trips-kind">
            Чья машина
            <select id="carsharing-trips-kind" value={вид}
                    onChange={(e) => сСброса(setВид)(e.target.value)}>
              <option value="">любая</option>
              <option value="service">служебная</option>
              <option value="personal">личная</option>
            </select>
          </label>
          <label htmlFor="carsharing-trips-region">
            Регион
            <select id="carsharing-trips-region" value={регион}
                    onChange={(e) => сСброса(setРегион)(e.target.value)}>
              <option value="">все</option>
              {ключи(регионы).map((к) => <option key={к} value={к}>{к}</option>)}
            </select>
          </label>
          <label htmlFor="carsharing-trips-q">
            Водитель или куда
            <input id="carsharing-trips-q" value={поиск}
                   onChange={(e) => сСброса(setПоиск)(e.target.value)} />
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={вПути}
                   onChange={(e) => сСброса(setВПути)(e.target.checked)} />
            только в пути
          </label>
        </FilterBar>
        <div className="hint" style={{ marginTop: 8 }}>
          найдено: {запрос.data?.total ?? 0}
          {итоги ? ` · ${pluralN(итоги.people ?? 0, 'водитель', 'водителя', 'водителей')}` : ''}
        </div>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        {итоги && <ПлиткиПоездок итоги={итоги} />}
        <ТаблицаПоездок строки={строки} вПути={вПути} />
        <Страницы всего={запрос.data?.total ?? 0} start={start} setStart={setStart} />
      </QueryState>
    </>
  )
}

function ПлиткиПоездок({ итоги }: { итоги: ПоездкиИтоги }) {
  const пропущено = (итоги.km_skipped ?? 0) + (итоги.km_missing ?? 0)
  return (
    <div className="tiles" style={{ marginBottom: 12 }}>
      <div className="tile">
        <div className="t-label">поездок в отборе</div>
        <div className="t-sum">{итоги.n ?? 0}</div>
        <div className="t-label">на личных авто: {итоги.personal_n ?? 0}</div>
      </div>
      <div className="tile">
        <div className="t-label">километров</div>
        <div className="t-sum">{итоги.km ?? 0}</div>
        <div className="t-label">
          {пропущено > 0
            ? `не сложено строк: ${пропущено} — одометр пуст или испорчен`
            : 'сложены все строки отбора'}
        </div>
      </div>
      <div className={(итоги.open_n ?? 0) > 0 ? 'tile hot' : 'tile'}>
        <div className="t-label">не закрыты</div>
        <div className="t-sum">{итоги.open_n ?? 0}</div>
        <div className="t-label">машина числится в пути</div>
      </div>
      <div className="tile">
        <div className="t-label">личных поездок</div>
        <div className="t-sum">{итоги.private_n ?? 0}</div>
        <div className="t-label">куда — не показываем</div>
      </div>
      {(итоги.flagged_n ?? 0) > 0 && (
        <div className="tile hot">
          <div className="t-label">портал пометил пробег спорным</div>
          <div className="t-sum">{итоги.flagged_n}</div>
          <div className="t-label">проверка портала, не CRM</div>
        </div>
      )}
    </div>
  )
}

function ТаблицаПоездок({ строки, вПути }: { строки: ПоездкаСтрока[]; вПути: boolean }) {
  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">
          {вПути ? 'Сейчас в пути никого' : 'Поездок под этот отбор нет'}
        </div>
        <div className="empty-hint">
          Поездку закрывают в портале 73 при возврате машины, в зеркале это видно через
          несколько минут. Правки уже закрытых поездок приезжают ночным полным чтением.
        </div>
      </div>
    )
  }
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th>Водитель</th>
            <th>Машина</th>
            <th>Выезд</th>
            <th>Куда</th>
            <th className="num">Км</th>
            <th>Сделка</th>
          </tr>
        </thead>
        <tbody>
          {строки.map((r) => <СтрокаПоездки key={r.d1_id} r={r} />)}
        </tbody>
      </table>
    </div>
  )
}

function СтрокаПоездки({ r }: { r: ПоездкаСтрока }) {
  return (
    <tr>
      <td data-label="Водитель">
        <div>{r.driver_name ?? '—'}</div>
        <div className="hint">{r.region ?? 'регион не указан'}</div>
      </td>
      <td data-label="Машина">
        <div>{r.car_title ?? '—'}</div>
        <div className="muted mono">
          {r.car_kind === 'personal' ? 'личная' : (r.car_plate ?? '')}
        </div>
      </td>
      <td data-label="Выезд" className="nowrap">
        {fmtDate(r.date_out, true)}
        {r.date_back
          ? <div className="hint">вернулся {fmtDate(r.date_back, true)}</div>
          : <div className="badge work">в пути</div>}
      </td>
      <td data-label="Куда">
        {r.destination
          ? r.destination
          : r.destination_hidden
            ? <span className="hint">личная поездка — куда, не показываем</span>
            : <span className="hint">не указано</span>}
        {r.return_issues && (
          <div className="error">при возврате: {r.return_issues}</div>
        )}
      </td>
      <td className="num" data-label="Км">
        {r.km ?? <span className="hint">—</span>}
        {r.flagged && <div className="error">спорный пробег</div>}
      </td>
      <td data-label="Сделка">
        {r.deal?.bitrix_id
          ? <EntityLink kind="deal" id={r.deal.bitrix_id} title={r.deal.title} />
          : <span className="hint">
              {r.purpose === 'personal' ? 'личная поездка' : 'без сделки'}
            </span>}
        {r.vessel?.name && (
          <div className="hint">
            <EntityLink kind="vessel" id={r.vessel.bitrix_id} title={r.vessel.name} />
          </div>
        )}
      </td>
    </tr>
  )
}

// ═══════════════════════════════ заправки ═══════════════════════════════
//
// Это КОМПЕНСАЦИЯ РАСХОДОВ, а не журнал заправок: компания возмещает сотруднику топливо
// по норме за километр. Поэтому здесь нет ни маршрута, ни госномера — есть человек,
// литры, километры по норме и сумма. Ядро подписывает такой ответ «только владельцу»,
// и подпись показана вверху, а не спрятана.

function Заправки() {
  const [месяц, setМесяц] = useState('')
  const [поиск, setПоиск] = useState('')
  const [start, setStart] = useState(0)

  const фильтр = useMemo(() => ({
    from: месяц ? `${месяц}-01` : '',
    to: месяц ? конецМесяца(месяц) : '',
    q: поиск,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [месяц, поиск, start])

  const запрос = useЗаправки(фильтр)
  const строки = запрос.data?.rows ?? []
  const итоги = запрос.data?.totals

  const сСброса = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }

  return (
    <>
      <Закрыто почему={запрос.data?.restricted_why} />
      <div className="card" style={{ marginBottom: 12 }}>
        <FilterBar id="carsharing-refuels">
          <label htmlFor="carsharing-refuels-month">
            Месяц
            <input id="carsharing-refuels-month" type="month" value={месяц}
                   onChange={(e) => сСброса(setМесяц)(e.target.value)} />
          </label>
          <label htmlFor="carsharing-refuels-q">
            Водитель
            <input id="carsharing-refuels-q" value={поиск}
                   onChange={(e) => сСброса(setПоиск)(e.target.value)} />
          </label>
        </FilterBar>
        <div className="hint" style={{ marginTop: 8 }}>
          найдено: {запрос.data?.total ?? 0}
          {итоги ? ` · ${pluralN(итоги.people ?? 0, 'человек', 'человека', 'человек')}` : ''}
        </div>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        {итоги && <ПлиткиЗаправок итоги={итоги} />}
        <ТаблицаЗаправок строки={строки} />
        <Страницы всего={запрос.data?.total ?? 0} start={start} setStart={setStart} />
      </QueryState>
    </>
  )
}

/** Последний день месяца ГГГГ-ММ. Считаем нулевым днём следующего — короче и без таблиц. */
function конецМесяца(месяц: string): string {
  const [г, м] = месяц.split('-').map(Number)
  if (!г || !м) return ''
  const д = new Date(Date.UTC(г, м, 0))
  return д.toISOString().slice(0, 10)
}

function ПлиткиЗаправок({ итоги }: { итоги: ЗаправкиИтоги }) {
  const без = (итоги.n ?? 0) - (итоги.amount_rows ?? 0)
  return (
    <div className="tiles" style={{ marginBottom: 12 }}>
      <div className="tile">
        <div className="t-label">заправок в отборе</div>
        <div className="t-sum">{итоги.n ?? 0}</div>
        <div className="t-label">
          машин: {итоги.cars ?? 0}, все личные
        </div>
      </div>
      <div className="tile">
        <div className="t-label">литров</div>
        <div className="t-sum">{итоги.liters ?? 0}</div>
        <div className="t-label">по норме портала: {итоги.paid_km ?? 0} км</div>
      </div>
      <div className="tile">
        <div className="t-label">сумма чеков</div>
        <div className="t-sum">{fmtMoney(итоги.amount_rub ?? 0)}</div>
        <div className="t-label">
          {без > 0
            ? `сумма стоит не везде: без неё строк ${без}`
            : 'сумма есть у всех строк отбора'}
        </div>
      </div>
      <div className="tile">
        <div className="t-label">период отбора</div>
        <div className="t-sum">{fmtDate(итоги.last_day)}</div>
        <div className="t-label">от {fmtDate(итоги.first_day)}</div>
      </div>
    </div>
  )
}

function ТаблицаЗаправок({ строки }: { строки: ЗаправкаСтрока[] }) {
  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">Заправок под этот отбор нет</div>
        <div className="empty-hint">
          Заправку личного авто заводит сам инженер на портале 73 при возврате машины.
          Пустой список — это «не заводили», а не «не возместили»: возмещение считает
          портал, и накопленный баланс живёт там же.
        </div>
      </div>
    )
  }
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th>Дата</th>
            <th>Водитель</th>
            <th>Машина</th>
            <th className="num">Литры</th>
            <th className="num">По норме, км</th>
            <th className="num">Сумма</th>
            <th>Сделка</th>
          </tr>
        </thead>
        <tbody>
          {строки.map((r) => <СтрокаЗаправки key={r.d1_id} r={r} />)}
        </tbody>
      </table>
    </div>
  )
}

function СтрокаЗаправки({ r }: { r: ЗаправкаСтрока }) {
  return (
    <tr>
      <td data-label="Дата" className="nowrap">{fmtDate(r.refuel_date)}</td>
      <td data-label="Водитель">
        <div>{r.driver_name ?? '—'}</div>
        <div className="hint">{r.region ?? 'регион не указан'}</div>
      </td>
      <td data-label="Машина">
        <div>{r.car_title ?? '—'}</div>
        {r.comment && <div className="hint">{r.comment}</div>}
      </td>
      <td className="num" data-label="Литры">
        {r.liters ?? <span className="hint">—</span>}
      </td>
      <td className="num" data-label="По норме, км">
        {r.paid_km ?? <span className="hint">—</span>}
      </td>
      <td className="num" data-label="Сумма">
        {r.amount_rub === null || r.amount_rub === undefined
          ? <span className="hint">не заполнена</span>
          : fmtMoney(r.amount_rub)}
      </td>
      <td data-label="Сделка">
        {r.deal?.bitrix_id
          ? <EntityLink kind="deal" id={r.deal.bitrix_id} title={r.deal.title} />
          : <span className="hint">без сделки</span>}
        {r.vessel?.name && (
          <div className="hint">
            <EntityLink kind="vessel" id={r.vessel.bitrix_id} title={r.vessel.name} />
          </div>
        )}
      </td>
    </tr>
  )
}
